<template>
  <div class="file-uploader">
    <el-upload
      ref="uploadRef"
      class="uploader"
      :http-request="handleUpload"
      :show-file-list="false"
      :limit="1"
      accept=".pdf,.doc,.docx,.xls,.xlsx"
    >
      <el-button type="primary" :loading="uploading">
        <el-icon><Upload /></el-icon>
        {{ uploading ? "上传中..." : "上传文件" }}
      </el-button>
      <div class="tip-text">支持PDF、Word、Excel文档（最大10MB）</div>
    </el-upload>

    <div v-if="currentFile.file" class="file-list">
      <div class="file-item">
        <el-icon class="file-icon"><Document /></el-icon>
        <div class="file-info">
          <span class="file-name">{{ currentFile.fileName }}</span>
          <a
            class="file-link"
            :href="buildFileUrl(currentFile.file)"
            target="_blank"
            :title="currentFile.file"
          >
            {{ truncateUrl(currentFile.file) }}
          </a>
        </div>
        <div class="delete-wrapper">
          <el-button
            v-if="levelCount > 1"
            type="danger"
            size="small"
            circle
            :loading="deleting"
            @click="handleDelete"
          >
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
      </div>
    </div>
    <div v-else class="empty-tip">暂无关联文件</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import type { UploadRequestHandler } from "element-plus";
import { Upload, Delete, Document } from "@element-plus/icons-vue";
import { fileApi } from "@/api/api";
import { FileEntity } from "@/api/fileEntity";
import { uploadImage } from "@/api/uploadImage";

const props = defineProps<{
  modelValue?: FileEntity | null;
  standardId?: number;
}>();
const emit = defineEmits(["update:modelValue"]);

// 响应式状态
const currentFile = ref<FileEntity>({
  id: null,
  file: "",
  fileName: "",
  standardId: 0,
});
const uploading = ref(false);
const deleting = ref(false);
const fileLoading = ref(false);

// 添加组件刷新方法
const refreshComponent = () => {
  currentFile.value = {
    id: null,
    file: "",
    fileName: "",
    standardId: props.standardId!,
  };
  fetchFileData(); // 强制重新获取数据
};

// 生命周期钩子
onMounted(async () => {
  await fetchFileData();
});

// 构建完整文件URL
// FileUploadComponent.vue
const buildFileUrl = (path?: string) => {
  if (!path) return "";
  // 统一使用HTTPS协议
  return path.startsWith("http")
    ? path.replace(/^http:/, "https:")
    : `https://your-cdn.com/${path}`;
};

// 截断长URL显示
const truncateUrl = (url = "") => {
  const maxLength = 35;
  return url.length > maxLength
    ? `${url.substring(0, 15)}...${url.slice(-15)}`
    : url;
};

// 获取文件数据
const fetchFileData = async () => {
  if (!props.standardId) return;

  fileLoading.value = true;
  try {
    const res = await fileApi.getFilesByStandardId(props.standardId);
    if (res.data) {
      currentFile.value = res.data;
      await syncToParent();
    }
  } catch (error) {
    ElMessage.error("文件加载失败");
    console.error("文件加载失败:", error);
  } finally {
    fileLoading.value = false;
    // 清理上传组件状态
    uploadRef.value?.clearFiles();
  }
};
// 添加上传组件的ref
const uploadRef = ref();

// 同步数据到父组件
const syncToParent = async () => {
  await nextTick(); // 等待DOM更新
  emit("update:modelValue", {
    ...currentFile.value,
    standardId: props.standardId,
  });
};
const operationLock = ref(false);

// 文件上传处理
const handleUpload: UploadRequestHandler = async ({ file }) => {
  if (operationLock.value) {
    ElMessage.warning("请等待当前操作完成");
    return;
  }
  operationLock.value = true;
  uploading.value = true;
  try {
    // 第一步：调用uploadFile上传文件获取文件路径
    const fileUrl = await uploadImage(file);
    if (!fileUrl) {
      throw new Error("文件上传失败");
    }

    // 第二步：构建FileEntity数据
    const fileEntity: FileEntity = {
      ...currentFile.value,
      file: fileUrl,
      fileName: file.name,
      standardId: props.standardId!,
    };

    // 3. 调用创建/更新接口
    const { code } = currentFile.value?.id
      ? await fileApi.updateFile(fileEntity)
      : await fileApi.createFile(fileEntity);

    if (code !== 1) throw new Error("接口操作失败");
    await fetchFileData();
    syncToParent();
    ElMessage.success("文件操作成功");
    operationLock.value = false;
  } catch (error) {
    console.error("上传失败:", error);
    ElMessage.error("文件上传失败");
  } finally {
    operationLock.value = false;
    uploading.value = false;
    // 清理上传组件状态
    uploadRef.value?.clearFiles();
  }
};
// 文件删除处理
const handleDelete = async () => {
  if (levelCount === 1) {
    ElMessage.warning("至少保留一个指标，无法删除！");
    return;
  }
  if (operationLock.value) {
    ElMessage.warning("请等待当前操作完成");
    return;
  }
  try {
    await ElMessageBox.confirm("确定要删除该文件吗？", "删除确认", {
      type: "warning",
    });
    operationLock.value = true;
    deleting.value = true;
    console.log("⭐删除文件currentFile.value.id", currentFile.value.id);

    if (currentFile.value?.id) {
      deleting.value = true;
      const { code } = await fileApi.deleteFile(currentFile.value.id);
      if (code !== 1) throw new Error("删除失败");
    }
    // 彻底重置状态
    currentFile.value = {
      id: null,
      file: "",
      fileName: "",
      standardId: props.standardId!, // 保留关联ID
    };
    await fetchFileData();
    await syncToParent();
    ElMessage.success("删除成功");
    // operationLock.value = false
  } catch (error) {
    if (error !== "cancel") {
      console.error("删除失败:", error);
      ElMessage.error(error.message);
      // 删除失败时重新获取文件状态
      await fetchFileData();
    }
  } finally {
    operationLock.value = false;
    deleting.value = false;
    // 清理上传组件状态
    uploadRef.value?.clearFiles();
  }
};
const resetComponent = async () => {
  operationLock.value = false;
  uploading.value = false;
  deleting.value = false;
  fileLoading.value = false;

  currentFile.value = {
    id: null,
    file: "",
    fileName: "",
    standardId: props.standardId!,
  };

  uploadRef.value?.clearFiles();
  await syncToParent();
};

// 响应standardId变化
watch(
  () => props.standardId,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      await resetComponent(); // 先重置组件状态
      currentFile.value.standardId = newVal;
      await fetchFileData();
    }
  },
  { immediate: true }
);

// 响应外部数据变化
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      currentFile.value = { ...newVal };
      // 确保上传组件状态同步
      uploadRef.value?.clearFiles();
    }
  },
  { deep: true }
);

// 暴露刷新方法给父组件
defineExpose({
  refreshComponent,
  resetComponent,
});
</script>

<style scoped>
.file-uploader {
  width: 100%;
}

.file-list {
  margin-top: 1rem;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 0.8rem;
  background: #f8f9fa;
  border-radius: 6px;
  transition: background 0.2s;
}

.file-item:hover {
  background: #e9ecef;
}

.file-icon {
  font-size: 1.4rem;
  color: #6c757d;
  margin-right: 0.8rem;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  display: block;
  font-weight: 500;
  color: #343a40;
  margin-bottom: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-link {
  font-size: 0.9em;
  color: #4a90e2;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.file-link:hover {
  text-decoration: underline;
}

.delete-wrapper {
  opacity: 0;
  transition: opacity 0.2s;
  margin-left: 1rem;
}

.file-item:hover .delete-wrapper {
  opacity: 1;
}

.empty-tip {
  color: #6c757d;
  font-size: 0.9em;
  margin-top: 0.5rem;
}
</style>
